import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import PageHeader from './PageHeader';
import LeadSuccessStatsModal from './commission-tabs/LeadSuccessStatsModal';
import * as XLSX from 'xlsx';

const CallCenterAdmin = ({ isDarkMode, userRole }) => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('lastUserId') || '';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loadingStats, setLoadingStats] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [detailModal, setDetailModal] = useState(null); // { buttonDescription, userId, userName, companies, loading }
  const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [ratingMonth, setRatingMonth] = useState(currentMonthKey);
  const [ratingCallcenterUser, setRatingCallcenterUser] = useState('');
  const [ratingAssignedUser, setRatingAssignedUser] = useState('');
  const [ratingStatusFilter, setRatingStatusFilter] = useState(''); // '' | 'rated' | 'unrated'
  const [ratingSearch, setRatingSearch] = useState('');
  const [ratingRows, setRatingRows] = useState([]);
  const [ratingMonths, setRatingMonths] = useState([]);
  const [ratingCallcenterUsers, setRatingCallcenterUsers] = useState([]);
  const [ratingAssignedUsers, setRatingAssignedUsers] = useState([]);
  const [ratingOverallAvg, setRatingOverallAvg] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [revertingLeadId, setRevertingLeadId] = useState('');
  const [deletingStatId, setDeletingStatId] = useState('');
  const [deletingRatingCompanyId, setDeletingRatingCompanyId] = useState('');
  const [moveBatchModal, setMoveBatchModal] = useState(null); // { row }
  const [moveBatchTargetId, setMoveBatchTargetId] = useState('');
  const [movingLeadId, setMovingLeadId] = useState('');
  const [statMoveOwnerModal, setStatMoveOwnerModal] = useState(null); // { row }
  const [statMoveOwnerTargetId, setStatMoveOwnerTargetId] = useState('');
  const [movingOwnerStatId, setMovingOwnerStatId] = useState('');
  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, x, y } hamburger menu řádku detailu
  const [openComplaintMenu, setOpenComplaintMenu] = useState(null); // { id, x, y } hamburger menu řádku reklamace
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' });
  const [addLeadSearchLoading, setAddLeadSearchLoading] = useState(false);
  const [addLeadSubmitting, setAddLeadSubmitting] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState('stats');

  // Provolat lists state
  const [provolatLists, setProvolatLists] = useState([]);
  const [provolatLoading, setProvolatLoading] = useState(false);
  const [newProvolatLabel, setNewProvolatLabel] = useState('');
  const [provolatCreating, setProvolatCreating] = useState(false);
  const [provolatOpenListId, setProvolatOpenListId] = useState(null);
  const [provolatIcosText, setProvolatIcosText] = useState({});
  const [provolatSavingIcos, setProvolatSavingIcos] = useState(false);

  // Reklamace leadů state
  const [leadComplaints, setLeadComplaints] = useState([]);
  const [leadComplaintsLoading, setLeadComplaintsLoading] = useState(false);
  const [leadComplaintStatusFilter, setLeadComplaintStatusFilter] = useState('');
  // Filtry reklamací (klientské): měsíc přidělení, měsíc reklamace, kdo má lead teď, kdo volal
  const [complaintAssignMonth, setComplaintAssignMonth] = useState('');
  const [complaintClaimMonth, setComplaintClaimMonth] = useState('');
  const [complaintOwnerFilter, setComplaintOwnerFilter] = useState('');
  const [complaintCallerFilter, setComplaintCallerFilter] = useState('');
  const [replyModal, setReplyModal] = useState(null); // { complaint, reply }
  const [replySaving, setReplySaving] = useState(false);
  const [moveOwnerModal, setMoveOwnerModal] = useState(null); // { complaint, targetUserId }
  const [complaintBatchModal, setComplaintBatchModal] = useState(null); // { complaint, targetButtonId }
  const [complaintActionId, setComplaintActionId] = useState('');
  // Dialog uznání reklamace s vyrovnáním provize (kdo platí + měsíc vyrovnání)
  const [approveModal, setApproveModal] = useState(null); // { complaint, payer, month, year }

  const openDetail = async (batchName, buttonDescription, userId, userName) => {
    const title = buttonDescription || batchName;
    setDetailModal({ buttonDescription: title, userId, userName, companies: [], loading: true });
    try {
      const detailParam = batchName
        ? `batchName=${encodeURIComponent(batchName)}`
        : `buttonDescription=${encodeURIComponent(buttonDescription)}`;
      const params = `?${detailParam}&month=${selectedMonth}&year=${selectedYear}${userId ? `&userId=${userId}` : ''}`;
      const res = await fetch(`${API_URL}/callcenter/stats/batch-detail${params}`);
      const data = res.ok ? await res.json() : [];
      setDetailModal({ buttonDescription: title, userId, userName, companies: data, loading: false });
    } catch {
      setDetailModal({ buttonDescription: title, userId, userName, companies: [], loading: false });
    }
  };

  const handleRevertLead = async (row) => {
    if (!row?.statId) return;
    const ok = window.confirm(`Opravdu chcete zrušit započtení leadu pro ${row.companyName || 'firmu'} a vrátit lead uživateli ${row.userName || 'callcentrum'}?`);
    if (!ok) return;

    try {
      setRevertingLeadId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/revert?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se zrušit započtení leadu');
      }

      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });

      await fetchStats();
      await fetchLeadRatings();
    } catch (error) {
      console.error('Chyba při vracení leadu:', error);
      alert(error.message || 'Chyba při vracení leadu');
    } finally {
      setRevertingLeadId('');
    }
  };

  const handleDeleteStat = async (row) => {
    if (!row?.statId) return;
    const ok = window.confirm(`Opravdu chcete smazat tento záznam ze statistiky pro ${row.companyName || 'firmu'}? Lead zůstane přiřazen, smaže se pouze záznam ze statistiky.`);
    if (!ok) return;

    try {
      setDeletingStatId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/delete?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se smazat záznam ze statistiky');
      }

      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });

      await fetchStats();
    } catch (error) {
      console.error('Chyba při mazání záznamu ze statistiky:', error);
      alert(error.message || 'Chyba při mazání záznamu ze statistiky');
    } finally {
      setDeletingStatId('');
    }
  };

  const handleDeleteLeadRating = async (row) => {
    if (!row?.companyId) return;
    const ok = window.confirm(`Opravdu chcete smazat hodnocení leadu pro ${row.companyName || 'tuto firmu'}?`);
    if (!ok) return;

    try {
      setDeletingRatingCompanyId(String(row.companyId));
      const response = await fetch(`${API_URL}/companies/${row.companyId}/lead-rating?userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Nepodařilo se smazat hodnocení leadu');
      }

      setRatingRows(prev => prev.filter(r => String(r.companyId) !== String(row.companyId)));
      await fetchLeadRatings();
    } catch (error) {
      console.error('Chyba při mazání hodnocení leadu:', error);
      alert(error.message || 'Chyba při mazání hodnocení leadu');
    } finally {
      setDeletingRatingCompanyId('');
    }
  };

  const handleMoveLead = async () => {
    const row = moveBatchModal?.row;
    if (!row?.statId || !moveBatchTargetId) return;

    const targetButton = config?.buttons?.find(b => b._id === moveBatchTargetId);
    if (!targetButton) return;

    const ok = window.confirm(`Opravdu chcete přesunout lead pro ${row.companyName || 'firmu'} do šarže "${targetButton.batchName}"?`);
    if (!ok) return;

    try {
      setMovingLeadId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/move-batch?userId=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newButtonId: moveBatchTargetId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se přesunout lead');
      }

      // Odeber lead z aktuálního detailu a zavři modal přesunu
      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).filter(c => String(c.statId) !== String(row.statId))
        };
      });
      setMoveBatchModal(null);
      setMoveBatchTargetId('');
      await fetchStats();
    } catch (error) {
      console.error('Chyba při přesunu leadu:', error);
      alert(error.message || 'Chyba při přesunu leadu');
    } finally {
      setMovingLeadId('');
    }
  };

  const handleMoveStatOwner = async () => {
    const row = statMoveOwnerModal?.row;
    if (!row?.statId || !statMoveOwnerTargetId) return;

    const targetUser = allUsers.find(u => String(u._id) === String(statMoveOwnerTargetId));
    const ok = window.confirm(`Opravdu chcete přesunout lead pro ${row.companyName || 'firmu'} na obchodníka "${targetUser?.name || ''}"?`);
    if (!ok) return;

    try {
      setMovingOwnerStatId(String(row.statId));
      const response = await fetch(`${API_URL}/callcenter/stats/${row.statId}/move-owner?userId=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerId: statMoveOwnerTargetId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Nepodařilo se přesunout lead na obchodníka');
      }

      // Aktualizuj jméno vlastníka v aktuálním detailu
      setDetailModal(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          companies: (prev.companies || []).map(c =>
            String(c.statId) === String(row.statId)
              ? { ...c, ownerName: data.newOwnerName || targetUser?.name || c.ownerName }
              : c
          )
        };
      });
      setStatMoveOwnerModal(null);
      setStatMoveOwnerTargetId('');
      await fetchStats();
    } catch (error) {
      console.error('Chyba při přesunu leadu na obchodníka:', error);
      alert(error.message || 'Chyba při přesunu leadu na obchodníka');
    } finally {
      setMovingOwnerStatId('');
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    // Načti uživatele pro formulář manuálního přidání leadu
    const loadUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/users?userId=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setAllUsers(Array.isArray(data) ? data : (data.users || []));
        }
      } catch (e) {
        console.error('Chyba při načítání uživatelů:', e);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    fetchLeadRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingMonth, ratingCallcenterUser, ratingAssignedUser, ratingSearch]);

  const searchCompanyForLead = async () => {
    const q = addLeadForm.searchQuery.trim();
    if (!q) return;
    setAddLeadSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/companies?search=${encodeURIComponent(q)}&userId=${userId}&globalSearch=true&limit=10`);
      const data = res.ok ? await res.json() : {};
      setAddLeadForm(prev => ({ ...prev, foundCompanies: Array.isArray(data) ? data : (data.companies || []), selectedCompany: null }));
    } catch {
      setAddLeadForm(prev => ({ ...prev, foundCompanies: [] }));
    } finally {
      setAddLeadSearchLoading(false);
    }
  };

  const handleAddManualLead = async () => {
    const { selectedCompany, selectedButtonId, callcenterUserId, assignedToUserId } = addLeadForm;
    if (!selectedCompany || !selectedButtonId || !callcenterUserId || !assignedToUserId) {
      alert('Vyplňte všechna povinná pole');
      return;
    }
    setAddLeadSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/callcenter/stats/manual?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: selectedCompany._id,
          buttonId: selectedButtonId,
          callcenterUserId,
          assignedToUserId,
          month: selectedMonth,
          year: selectedYear
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Chyba při přidávání leadu');
      alert('Lead byl úspěšně přidán do statistiky a provizí');
      setShowAddLeadModal(false);
      setAddLeadForm({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' });
      await fetchStats();
    } catch (error) {
      alert(error.message || 'Chyba při přidávání leadu');
    } finally {
      setAddLeadSubmitting(false);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      console.log('📞 Načítám call center konfiguraci z:', `${API_URL}/callcenter/config`);
      const response = await fetch(`${API_URL}/callcenter/config?userId=${userId}`);
      console.log('📞 Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('📞 Načtená konfigurace:', data);
      setConfig(data);
    } catch (error) {
      console.error('❌ Chyba při načítání konfigurace:', error);
      // Nastavit výchozí prázdnou konfiguraci
      setConfig({ buttons: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await fetch(
        `${API_URL}/callcenter/stats/summary?month=${selectedMonth}&year=${selectedYear}&userId=${userId}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Chyba při načítání statistik:', error);
      setStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchLeadRatings = async () => {
    try {
      setRatingLoading(true);
      const params = new URLSearchParams({
        month: ratingMonth,
        userId
      });
      if (ratingCallcenterUser) params.set('callCenterUserId', ratingCallcenterUser);
      if (ratingAssignedUser) params.set('assignedToUserId', ratingAssignedUser);
      if (ratingSearch.trim()) params.set('search', ratingSearch.trim());

      const response = await fetch(`${API_URL}/callcenter/lead-ratings?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRatingRows(data.rows || []);
      setRatingMonths(data.monthsAvailable || []);
      setRatingCallcenterUsers(data.callcenterUsers || []);
      setRatingAssignedUsers(data.assignedToUsers || []);
      setRatingOverallAvg(Number(data.overallAvg || 0));

      if ((data.monthsAvailable || []).length > 0 && !data.monthsAvailable.includes(ratingMonth)) {
        setRatingMonth(data.monthsAvailable[0]);
      }
    } catch (error) {
      console.error('Chyba při načítání hodnocení leadů:', error);
      setRatingRows([]);
      setRatingOverallAvg(0);
    } finally {
      setRatingLoading(false);
    }
  };



  const handleAddButton = () => {
    const newButton = {
      description: 'Nové tlačítko',
      assignTo: 'kontakty@vyjedname.cz',
      batchName: 'Nová šarže',
      value: 0,
      color: 'purple',
      order: (config?.buttons?.length || 0) + 1,
      isActive: true,
      _id: `temp-${Date.now()}`
    };
    
    setConfig({
      ...config,
      buttons: [...(config?.buttons || []), newButton]
    });
    
    setEditingButton(newButton._id);
  };

  const handleUpdateButton = (buttonId, field, value) => {
    setConfig({
      ...config,
      buttons: config.buttons.map(btn =>
        btn._id === buttonId ? { ...btn, [field]: value } : btn
      )
    });
  };

  const handleDeleteButton = (buttonId) => {
    if (!window.confirm('Opravdu chcete smazat toto tlačítko?')) return;
    
    setConfig({
      ...config,
      buttons: config.buttons.filter(btn => btn._id !== buttonId)
    });
  };

  const handleMoveButton = (index, direction) => {
    const newButtons = [...config.buttons];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newButtons.length) return;
    
    [newButtons[index], newButtons[targetIndex]] = [newButtons[targetIndex], newButtons[index]];
    
    // Update order
    newButtons.forEach((btn, idx) => {
      btn.order = idx + 1;
    });
    
    setConfig({ ...config, buttons: newButtons });
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      // Očistit dočasná ID
      const buttonsToSave = config.buttons.map(btn => {
        const { _id, ...rest } = btn;
        if (_id && _id.toString().startsWith('temp-')) {
          return rest;
        }
        return btn;
      });
      
      const response = await fetch(`${API_URL}/callcenter/config?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buttons: buttonsToSave })
      });
      
      if (!response.ok) throw new Error('Chyba při ukládání');
      
      const savedConfig = await response.json();
      setConfig(savedConfig);
      setEditingButton(null);
      alert('Konfigurace byla úspěšně uložena');
    } catch (error) {
      console.error('Chyba při ukládání konfigurace:', error);
      alert('Chyba při ukládání konfigurace');
    } finally {
      setSaving(false);
    }
  };

  // =================== PROVOLAT LISTS FUNCTIONS ===================
  const fetchProvolatLists = async () => {
    try {
      setProvolatLoading(true);
      const res = await fetch(`${API_URL}/callcenter/provolat-lists?userId=${userId}`);
      const data = res.ok ? await res.json() : [];
      setProvolatLists(Array.isArray(data) ? data : []);
    } catch {
      setProvolatLists([]);
    } finally {
      setProvolatLoading(false);
    }
  };

  const fetchLeadComplaints = async (silent = false) => {
    try {
      if (!silent) setLeadComplaintsLoading(true);
      const params = `?userId=${userId}&isAdmin=true${leadComplaintStatusFilter ? `&status=${leadComplaintStatusFilter}` : ''}`;
      const res = await fetch(`${API_URL}/lead-complaints${params}`);
      const data = res.ok ? await res.json() : { complaints: [] };
      setLeadComplaints(Array.isArray(data.complaints) ? data.complaints : []);
    } catch {
      setLeadComplaints([]);
    } finally {
      if (!silent) setLeadComplaintsLoading(false);
    }
  };

  const updateLeadComplaint = async (id, body) => {
    setComplaintActionId(id);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${id}?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, actorUserId: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při aktualizaci reklamace');
      }
      await fetchLeadComplaints(true);
    } catch {
      alert('Chyba připojení');
    } finally {
      setComplaintActionId('');
    }
  };

  const handleReturnLead = async (complaint) => {
    if (!window.confirm(`Vrátit lead "${complaint.companyName}" callcentru (${complaint.calledByName || 'callcentrum'})?`)) return;
    setComplaintActionId(complaint._id);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${complaint._id}/return-lead?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actorUserId: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při vracení leadu');
      }
      await fetchLeadComplaints(true);
    } catch {
      alert('Chyba připojení');
    } finally {
      setComplaintActionId('');
    }
  };

  const handleExcludeFromStats = async (complaint) => {
    if (!window.confirm(`Smazat lead "${complaint.companyName}" ze statistiky callcentra? Odpovídající záznamy se odeberou.`)) return;
    setComplaintActionId(complaint._id);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${complaint._id}/exclude-from-stats?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actorUserId: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při odebírání ze statistiky');
      }
      await fetchLeadComplaints(true);
    } catch {
      alert('Chyba připojení');
    } finally {
      setComplaintActionId('');
    }
  };

  const handleDeleteLeadComplaint = async (complaint) => {
    if (!window.confirm(`Opravdu smazat reklamaci pro "${complaint.companyName}"?`)) return;
    setComplaintActionId(complaint._id);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${complaint._id}?userId=${userId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při mazání reklamace');
      }
      await fetchLeadComplaints(true);
    } catch {
      alert('Chyba připojení');
    } finally {
      setComplaintActionId('');
    }
  };

  const handleSaveReply = async () => {
    if (!replyModal) return;
    setReplySaving(true);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${replyModal.complaint._id}?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminReply: replyModal.reply, actorUserId: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při ukládání odpovědi');
      } else {
        setReplyModal(null);
        await fetchLeadComplaints(true);
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setReplySaving(false);
    }
  };

  const handleMoveOwner = async () => {
    if (!moveOwnerModal || !moveOwnerModal.targetUserId) return;
    await updateLeadComplaint(moveOwnerModal.complaint._id, { currentOwnerId: moveOwnerModal.targetUserId });
    setMoveOwnerModal(null);
  };

  const handleChangeComplaintBatch = async () => {
    if (!complaintBatchModal || !complaintBatchModal.targetButtonId) return;
    const { complaint, targetButtonId } = complaintBatchModal;
    const targetButton = (config?.buttons || []).find(b => b._id === targetButtonId);
    if (!targetButton) return;
    if (!window.confirm(`Změnit šarži leadu "${complaint.companyName}" na "${targetButton.batchName}"? Změní se i cena leadu (provize).`)) return;
    setComplaintActionId(complaint._id);
    try {
      const res = await fetch(`${API_URL}/lead-complaints/${complaint._id}/move-batch?userId=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newButtonId: targetButtonId, actorUserId: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při změně šarže');
      } else {
        setComplaintBatchModal(null);
        await fetchLeadComplaints(true);
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setComplaintActionId('');
    }
  };

  const handleApproveWithSettlement = async () => {
    if (!approveModal) return;
    const { complaint, payer, month, year } = approveModal;
    if (!payer || !month || !year) {
      alert('Vyberte plátce a měsíc vyrovnání');
      return;
    }
    await updateLeadComplaint(complaint._id, {
      status: 'approved',
      settlementPayer: payer,
      settlementMonth: parseInt(month),
      settlementYear: parseInt(year)
    });
    setApproveModal(null);
  };

  const handleCreateProvolatList = async () => {
    if (!newProvolatLabel.trim()) return;
    setProvolatCreating(true);
    try {
      const res = await fetch(`${API_URL}/callcenter/provolat-lists?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newProvolatLabel.trim() })
      });
      if (res.ok) {
        setNewProvolatLabel('');
        await fetchProvolatLists();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Chyba při vytváření seznamu');
      }
    } catch {
      alert('Chyba při připojení k serveru');
    } finally {
      setProvolatCreating(false);
    }
  };

  const handleSaveProvolatIcos = async (listId) => {
    const text = provolatIcosText[listId] || '';
    const icos = text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    setProvolatSavingIcos(true);
    try {
      const res = await fetch(`${API_URL}/callcenter/provolat-lists/${listId}?userId=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ icos })
      });
      if (res.ok) {
        await fetchProvolatLists();
        alert(`Uloženo ${icos.length} IČO`);
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Chyba při ukládání IČO');
      }
    } catch {
      alert('Chyba při připojení k serveru');
    } finally {
      setProvolatSavingIcos(false);
    }
  };

  const handleDeleteProvolatList = async (listId, label) => {
    if (!window.confirm(`Opravdu smazat seznam "${label}"? Tato akce je nevratná.`)) return;
    try {
      await fetch(`${API_URL}/callcenter/provolat-lists/${listId}?userId=${userId}`, { method: 'DELETE' });
      await fetchProvolatLists();
      if (provolatOpenListId === listId) setProvolatOpenListId(null);
    } catch {
      alert('Chyba při mazání seznamu');
    }
  };

  const handleRemoveExtension = async (listId, ico) => {
    try {
      await fetch(`${API_URL}/callcenter/provolat-lists/${listId}/extensions/${encodeURIComponent(ico)}?userId=${userId}`, { method: 'DELETE' });
      await fetchProvolatLists();
    } catch {
      alert('Chyba při mazání záznamu');
    }
  };

  const handleExportExtensions = (list) => {
    const extensions = list.extensions || [];
    if (extensions.length === 0) {
      alert('Žádné záznamy k exportu');
      return;
    }
    const wsData = [['IČO', 'Prodloužit'], ...extensions.map(e => [e.ico, list.label])];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prodloužení');
    XLSX.writeFile(wb, `provolat-prodlouzeni-${list._id}.xlsx`);
  };
  // =================== END PROVOLAT LISTS FUNCTIONS ===================

  const getMonthName = (month) => {
    const months = [
      'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
      'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
    ];
    return months[month - 1];
  };

  const colorOptions = [
    { value: 'purple', label: 'Fialová', class: 'bg-purple-500' },
    { value: 'blue', label: 'Modrá', class: 'bg-blue-500' },
    { value: 'green', label: 'Zelená', class: 'bg-green-500' },
    { value: 'red', label: 'Červená', class: 'bg-red-500' },
    { value: 'orange', label: 'Oranžová', class: 'bg-orange-500' },
    { value: 'pink', label: 'Růžová', class: 'bg-pink-500' },
    { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500' }
  ];

  // Seskupit statistiky podle uživatelů
  const groupedStats = stats.reduce((acc, stat) => {
    // Klíčujeme podle userId (ne userName) – shodné s detailem (batch-detail filtruje podle userId).
    const key = stat.userId;
    if (!acc[key]) {
      acc[key] = {
        userName: stat.userName,
        userId: stat.userId,
        buttons: {}
      };
    }

    // Sdružujeme podle šarže (batchName), aby se manuálně předané leady ("… (manuálně)")
    // i přejmenovaná tlačítka započítala do správného sloupce a součet sloupců odpovídal celku.
    const btnKey = stat.batchName || stat.buttonDescription;
    if (!acc[key].buttons[btnKey]) {
      acc[key].buttons[btnKey] = {
        count: 0,
        totalValue: 0,
        companies: [],
        batchName: stat.batchName || '',
        description: stat.buttonDescription || ''
      };
    }

    acc[key].buttons[btnKey].count += stat.count;
    acc[key].buttons[btnKey].totalValue += (stat.totalValue || 0);
    acc[key].buttons[btnKey].companies.push(...stat.companies);

    return acc;
  }, {});

  // Šarže aktivních tlačítek (klíč sloupců). "Ostatní" pokryje leady mimo aktivní tlačítka.
  const activeBatchKeys = new Set((config?.buttons || []).filter(b => b.isActive).map(b => b.batchName));
  const hasOtherStats = Object.values(groupedStats).some(us =>
    Object.keys(us.buttons).some(k => !activeBatchKeys.has(k))
  );

  const selectedCallcenterAvg = (() => {
    const ratedRows = ratingRows.filter(r => r.hasRating);
    if (ratedRows.length === 0) return 0;
    const baseRows = ratingCallcenterUser
      ? ratedRows.filter(r => String(r.callCenterUserId) === String(ratingCallcenterUser))
      : ratedRows;
    if (baseRows.length === 0) return 0;
    const total = baseRows.reduce((sum, r) => sum + Number(r.score || 0), 0);
    return Math.round((total / baseRows.length) * 100) / 100;
  })();

  const filteredRatingRows = ratingRows.filter(r => {
    if (ratingStatusFilter === 'rated') return r.hasRating;
    if (ratingStatusFilter === 'unrated') return !r.hasRating;
    return true;
  });

  // Reklamace – klientské filtry (měsíc přidělení/reklamace, kdo má lead teď, kdo volal)
  const ymKey = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
  };
  const ymLabel = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-');
    return `${getMonthName(parseInt(m))} ${y}`;
  };
  const complaintAssignMonthOptions = [...new Set(leadComplaints.map(c => ymKey(c.assignedAt)).filter(Boolean))].sort().reverse();
  const complaintClaimMonthOptions = [...new Set(leadComplaints.map(c => ymKey(c.complainedAt)).filter(Boolean))].sort().reverse();
  const complaintOwnerOptions = [...new Set(leadComplaints.map(c => c.currentOwnerName).filter(Boolean))].sort();
  const complaintCallerOptions = [...new Set(leadComplaints.map(c => c.calledByName).filter(Boolean))].sort();
  const filteredLeadComplaints = leadComplaints.filter(c => {
    if (complaintAssignMonth && ymKey(c.assignedAt) !== complaintAssignMonth) return false;
    if (complaintClaimMonth && ymKey(c.complainedAt) !== complaintClaimMonth) return false;
    if (complaintOwnerFilter && c.currentOwnerName !== complaintOwnerFilter) return false;
    if (complaintCallerFilter && c.calledByName !== complaintCallerFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className={`p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <div className="text-center">Načítání...</div>
      </div>
    );
  }

  // BEZPEČNOSTNÍ KONTROLA - pouze pro adminy (musí být AŽ PO hooks a před hlavním return)
  if (userRole !== 'admin' && userRole !== 'teamleader' && userRole !== 'superadmin') {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">🚫 Přístup odepřen</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Tato stránka je dostupná pouze administrátorům.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (<>
    <div className={`p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'} min-h-screen`}>
      <div className="max-w-[104rem] mx-auto">
        {/* Tlačítko zpět */}
        <PageHeader
          title="📞 Správa Call Centra"
          backPath="/users"
        />

        {/* Tab lišta */}
        <div className={`flex flex-wrap border-b mb-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {[
            { id: 'stats', label: '📊 Statistika leadů' },
            { id: 'lead-success', label: '📈 Úspěšnost OZ' },
            { id: 'ratings', label: '⭐ Hodnocení callcentra' },
            { id: 'provolat', label: '📋 Provolat seznamy' },
            { id: 'lead-complaints', label: '🚩 Reklamace leadů' },
            { id: 'config', label: '⚙️ Konfigurace' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'provolat' && provolatLists.length === 0) fetchProvolatLists(); if (tab.id === 'lead-complaints') fetchLeadComplaints(); }}
              className={`px-5 py-3 font-medium text-sm border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-500'
                  : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Konfigurace tlačítek */}
        {activeTab === 'config' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 mb-8`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">⚙️ Konfigurace tlačítek</h2>
            <div className="flex gap-2">
              <button
                onClick={handleAddButton}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Přidat tlačítko
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold disabled:bg-gray-400"
              >
                {saving ? 'Ukládání...' : 'Uložit změny'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {config?.buttons?.map((button, index) => (
              <div
                key={button._id}
                className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-lg p-4 border-2 ${
                  editingButton === button._id ? 'border-blue-500' : 'border-transparent'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Order controls */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleMoveButton(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-600 rounded disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <span className="text-center font-bold">{index + 1}</span>
                    <button
                      onClick={() => handleMoveButton(index, 'down')}
                      disabled={index === config.buttons.length - 1}
                      className="p-1 hover:bg-gray-600 rounded disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Popis */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Popis tlačítka:</label>
                      <input
                        type="text"
                        value={button.description}
                        onChange={(e) => handleUpdateButton(button._id, 'description', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="např. Lead 100 Kč - podklady nadiktované"
                      />
                    </div>

                    {/* Šarže */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Název šarže:</label>
                      <input
                        type="text"
                        value={button.batchName}
                        onChange={(e) => handleUpdateButton(button._id, 'batchName', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="např. Lead 100 Kč - nadiktované podklady"
                      />
                    </div>

                    {/* Přidělit komu */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Přidělit komu (email):</label>
                      <input
                        type="email"
                        value={button.assignTo}
                        onChange={(e) => handleUpdateButton(button._id, 'assignTo', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="kontakty@vyjedname.cz"
                      />
                    </div>

                    {/* Hodnota pro provize */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Hodnota (Kč) pro provize:</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={button.value || 0}
                        onChange={(e) => handleUpdateButton(button._id, 'value', parseFloat(e.target.value) || 0)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                        placeholder="100"
                      />
                    </div>

                    {/* Barva */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Barva:</label>
                      <select
                        value={button.color}
                        onChange={(e) => handleUpdateButton(button._id, 'color', e.target.value)}
                        onFocus={() => setEditingButton(button._id)}
                        className={`w-full px-3 py-2 rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {colorOptions.map(color => (
                          <option key={color.value} value={color.value}>
                            {color.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={button.isActive}
                        onChange={(e) => handleUpdateButton(button._id, 'isActive', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Aktivní</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!button.leadRatingEnabled}
                        onChange={(e) => handleUpdateButton(button._id, 'leadRatingEnabled', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Hodnocení leadů</span>
                    </label>
                    <button
                      onClick={() => handleDeleteButton(button._id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                    >
                      🗑️ Smazat
                    </button>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <p className="text-sm text-gray-400 mb-2">Náhled tlačítka:</p>
                  <div className="flex gap-2">
                    <button
                      className={`bg-${button.color}-500 hover:bg-${button.color}-600 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#8b5cf6'
                      }}
                    >
                      Odeslat
                    </button>
                    <button
                      className={`bg-${button.color}-600 hover:bg-${button.color}-700 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#7c3aed'
                      }}
                    >
                      Odeslat a zpět na seznam
                    </button>
                    <button
                      className={`bg-${button.color}-700 hover:bg-${button.color}-800 text-white py-2 px-4 rounded-lg transition font-semibold text-xs flex items-center justify-center gap-1 shadow-lg`}
                      style={{
                        backgroundColor: colorOptions.find(c => c.value === button.color)?.class.replace('bg-', '').includes(button.color) ? undefined : '#6d28d9'
                      }}
                    >
                      Odeslat a další firma
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {(!config?.buttons || config.buttons.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                Zatím nejsou vytvořena žádná tlačítka. Klikněte na "Přidat tlačítko" pro vytvoření prvního.
              </div>
            )}
          </div>
        </div>
        )}

        {/* Statistiky Call Centra */}
        {activeTab === 'stats' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">📊 Statistiky leadů</h2>
            <div className="flex gap-4 items-center">
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
              >
                ➕ Přidat lead manuálně
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className={`px-4 py-2 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className={`px-4 py-2 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                onClick={fetchStats}
                disabled={loadingStats}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                {loadingStats ? 'Načítání...' : 'Obnovit'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <th className="px-4 py-3 text-left font-semibold">Uživatel</th>
                  {config?.buttons?.filter(b => b.isActive).map(button => (
                    <th key={button._id} className="px-4 py-3 text-center font-semibold">
                      {button.description}
                    </th>
                  ))}
                  {hasOtherStats && (
                    <th className="px-4 py-3 text-center font-semibold">Ostatní</th>
                  )}
                  <th className="px-4 py-3 text-center font-semibold">Celkem</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(groupedStats).map((userStat, index) => {
                  const total = Object.values(userStat.buttons).reduce((sum, btn) => sum + btn.count, 0);
                  
                  const totalValue = Object.values(userStat.buttons).reduce(
                    (sum, btn) => sum + (btn.totalValue || 0), 
                    0
                  );
                  
                  return (
                    <tr
                      key={userStat.userId}
                      className={`border-b ${
                        isDarkMode ? 'border-gray-700' : 'border-gray-200'
                      } ${index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')}`}
                    >
                      <td className="px-4 py-3 font-medium">{userStat.userName}</td>
                      {config?.buttons?.filter(b => b.isActive).map(button => {
                        const count = userStat.buttons[button.batchName]?.count || 0;
                        const totalValue = userStat.buttons[button.batchName]?.totalValue || 0;
                        return (
                          <td key={button._id} className="px-4 py-3 text-center">
                            {count > 0 ? (
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => openDetail(button.batchName, button.description, userStat.userId, userStat.userName)}
                                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full font-semibold transition cursor-pointer"
                                  title="Zobrazit seznam firem"
                                >
                                  {count}
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {totalValue.toLocaleString('cs-CZ')} Kč
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        );
                      })}
                      {hasOtherStats && (() => {
                        const otherEntries = Object.entries(userStat.buttons).filter(([k]) => !activeBatchKeys.has(k));
                        const otherCount = otherEntries.reduce((s, [, b]) => s + b.count, 0);
                        const otherValue = otherEntries.reduce((s, [, b]) => s + (b.totalValue || 0), 0);
                        return (
                          <td className="px-4 py-3 text-center">
                            {otherCount > 0 ? (
                              <div className="flex flex-col items-center gap-1">
                                <span
                                  className="inline-block bg-gray-500 text-white px-3 py-1 rounded-full font-semibold"
                                  title={otherEntries.map(([, b]) => `${b.description || b.batchName}: ${b.count}`).join('\n')}
                                >
                                  {otherCount}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {otherValue.toLocaleString('cs-CZ')} Kč
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        );
                      })()}
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-block bg-green-600 text-white px-4 py-1 rounded-full font-bold">
                            {total}
                          </span>
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                            {totalValue.toLocaleString('cs-CZ')} Kč
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {Object.keys(groupedStats).length === 0 && (
                  <tr>
                    <td
                      colSpan={config?.buttons?.filter(b => b.isActive).length + (hasOtherStats ? 3 : 2)}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      Žádné statistiky pro vybraný měsíc
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Úspěšnost OZ */}
        {activeTab === 'lead-success' && (
          <LeadSuccessStatsModal
            embedded
            isDarkMode={isDarkMode}
            currentUserId={userId}
            initialMonth={currentMonthKey}
            batchOptions={(config?.buttons || []).filter(b => b.isActive).map(b => ({ batchName: b.batchName }))}
          />
        )}

        {/* Hodnocení leadů callcentra */}
        {activeTab === 'ratings' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 mt-8`}>
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-2xl font-semibold">⭐ Hodnocení callcentra</h2>
              <button
                onClick={fetchLeadRatings}
                disabled={ratingLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg self-start md:self-auto"
              >
                {ratingLoading ? 'Načítání...' : 'Obnovit'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Měsíc hodnocení</label>
                <select
                  value={ratingMonth}
                  onChange={(e) => setRatingMonth(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {(ratingMonths.length > 0 ? ratingMonths : [ratingMonth]).map(monthKey => (
                    <option key={monthKey} value={monthKey}>{monthKey}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Callcentrum uživatel</label>
                <select
                  value={ratingCallcenterUser}
                  onChange={(e) => setRatingCallcenterUser(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Všichni</option>
                  {ratingCallcenterUsers.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Komu přiděleno</label>
                <select
                  value={ratingAssignedUser}
                  onChange={(e) => setRatingAssignedUser(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Všichni</option>
                  {ratingAssignedUsers.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Stav hodnocení</label>
                <select
                  value={ratingStatusFilter}
                  onChange={(e) => setRatingStatusFilter(e.target.value)}
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Vše</option>
                  <option value="rated">Hodnoceno</option>
                  <option value="unrated">Nehodnoceno</option>
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium mb-1">Hledat IČO / zákazníka</label>
                <input
                  type="text"
                  value={ratingSearch}
                  onChange={(e) => setRatingSearch(e.target.value)}
                  placeholder="Např. 12345678 nebo Název firmy"
                  className={`w-full px-3 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className={`rounded-lg px-4 py-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400">Průměr hodnocení {ratingCallcenterUser ? 'vybraného callcentra' : 'za měsíc'}</div>
                <div className="text-2xl font-bold text-yellow-500">{selectedCallcenterAvg.toFixed(2)} / 5</div>
              </div>
              <div className={`rounded-lg px-4 py-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400">Celkový průměr (aktuální filtr)</div>
                <div className="text-2xl font-bold text-blue-500">{ratingOverallAvg.toFixed(2)} / 5</div>
              </div>
              <div className={`rounded-lg px-4 py-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                <div className="text-sm text-gray-500 dark:text-gray-400">Hodnoceno / Nehodnoceno</div>
                <div className="text-2xl font-bold">
                  <span className="text-green-500">{ratingRows.filter(r => r.hasRating).length}</span>
                  {' / '}
                  <span className="text-gray-400">{ratingRows.filter(r => !r.hasRating).length}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <th className="px-4 py-3 text-left font-semibold">Měsíc</th>
                  <th className="px-4 py-3 text-left font-semibold">Kdo lead vytvořil</th>
                  <th className="px-4 py-3 text-left font-semibold">Komu přiděleno</th>
                  <th className="px-4 py-3 text-left font-semibold">IČO zákazníka</th>
                  <th className="px-4 py-3 text-left font-semibold">Jméno zákazníka</th>
                  <th className="px-4 py-3 text-center font-semibold">Hodnocení</th>
                  <th className="px-4 py-3 text-left font-semibold">Datum hodnocení</th>
                  <th className="px-4 py-3 text-right font-semibold">Akce</th>
                </tr>
              </thead>
              <tbody>
                {filteredRatingRows.map((row, index) => (
                  <tr
                    key={`${row.companyId}-${index}`}
                    className={`border-b ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    } ${index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')}`}
                  >
                    <td className="px-4 py-3">{row.month}</td>
                    <td className="px-4 py-3 font-medium">{row.callCenterUserName || '—'}</td>
                    <td className="px-4 py-3">{row.assignedToName || '—'}</td>
                    <td className="px-4 py-3">{row.companyIco || '—'}</td>
                    <td className="px-4 py-3">{row.companyName || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {row.hasRating ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-yellow-500">
                          {`${Number(row.score || 0).toFixed(1)} ★`}
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">Nehodnoceno</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{row.ratedAt ? new Date(row.ratedAt).toLocaleString('cs-CZ') : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {row.hasRating && (
                        <button
                          onClick={() => handleDeleteLeadRating(row)}
                          disabled={deletingRatingCompanyId === String(row.companyId)}
                          className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-500"
                        >
                          {deletingRatingCompanyId === String(row.companyId) ? 'Mažu...' : 'Smazat'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {filteredRatingRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Žádné leady pro vybrané filtry
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Provolat seznamy */}
        {activeTab === 'provolat' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6`}>
          <h2 className="text-2xl font-semibold mb-6">📋 Provolat seznamy</h2>

          {/* Nový seznam */}
          <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
            <h3 className="font-semibold mb-3">Vytvořit nový seznam</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newProvolatLabel}
                onChange={e => setNewProvolatLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateProvolatList()}
                placeholder='Např. "Provolat do 20.7.2026"'
                className={`flex-1 px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
              />
              <button
                onClick={handleCreateProvolatList}
                disabled={provolatCreating || !newProvolatLabel.trim()}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:bg-gray-400"
              >
                {provolatCreating ? 'Vytvářím...' : 'Vytvořit'}
              </button>
            </div>
          </div>

          {/* Loading */}
          {provolatLoading && <div className="text-center py-8 text-gray-500">Načítám...</div>}

          {/* Lists */}
          {!provolatLoading && provolatLists.length === 0 && (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Zatím nemáte žádné Provolat seznamy. Vytvořte první výše.
            </div>
          )}

          <div className="space-y-4">
            {provolatLists.map(list => {
              const isOpen = provolatOpenListId === list._id;
              return (
                <div key={list._id} className={`rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                  {/* List header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setProvolatOpenListId(isOpen ? null : list._id)}
                        className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                      >
                        {isOpen ? '▼' : '▶'} {list.label}
                      </button>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {(list.icos || []).length} IČO
                      </span>
                      {(list.extensions || []).length > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          {list.extensions.length} prodloužení
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteProvolatList(list._id, list.label)}
                      className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                    >
                      Smazat
                    </button>
                  </div>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                      {/* IČO import */}
                      <div className="mt-4">
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          IČO (jedno na řádek, nebo oddělena čárkou / středníkem) — aktuálně {(list.icos || []).length} IČO
                        </label>
                        <textarea
                          rows={6}
                          value={provolatIcosText[list._id] !== undefined ? provolatIcosText[list._id] : (list.icos || []).join('\n')}
                          onChange={e => setProvolatIcosText(prev => ({ ...prev, [list._id]: e.target.value }))}
                          placeholder="12345678&#10;87654321&#10;..."
                          className={`w-full px-3 py-2 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
                        />
                        <button
                          onClick={() => handleSaveProvolatIcos(list._id)}
                          disabled={provolatSavingIcos}
                          className="mt-2 px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:bg-gray-400"
                        >
                          {provolatSavingIcos ? 'Ukládám...' : 'Uložit IČO'}
                        </button>
                      </div>

                      {/* Extension requests */}
                      {(list.extensions || []).length > 0 && (
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              📅 Žádosti o prodloužení o +1 měsíc ({list.extensions.length})
                            </h4>
                            <button
                              onClick={() => handleExportExtensions(list)}
                              className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                            >
                              ⬇ Export do Excelu
                            </button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                                  <th className="px-3 py-2 text-left font-semibold">IČO</th>
                                  <th className="px-3 py-2 text-left font-semibold">Datum žádosti</th>
                                  <th className="px-3 py-2 text-right font-semibold">Akce</th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.extensions.map((ext, i) => (
                                  <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                    <td className="px-3 py-2 font-mono">{ext.ico}</td>
                                    <td className="px-3 py-2">{ext.requestedAt ? new Date(ext.requestedAt).toLocaleString('cs-CZ') : '—'}</td>
                                    <td className="px-3 py-2 text-right">
                                      <button
                                        onClick={() => handleRemoveExtension(list._id, ext.ico)}
                                        className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                                      >
                                        Smazat
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {(list.extensions || []).length === 0 && (
                        <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Žádné žádosti o prodloužení.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Reklamace leadů */}
        {activeTab === 'lead-complaints' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-6 mb-8`}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-bold">🚩 Reklamace leadů</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={leadComplaintStatusFilter}
                onChange={e => setLeadComplaintStatusFilter(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">Všechny stavy</option>
                <option value="open">Nové</option>
                <option value="in-progress">V řešení</option>
                <option value="approved">Uznáno</option>
                <option value="rejected">Neuznáno</option>
              </select>
              <select
                value={complaintAssignMonth}
                onChange={e => setComplaintAssignMonth(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                title="Měsíc přidělení"
              >
                <option value="">Přidělení: vše</option>
                {complaintAssignMonthOptions.map(ym => (
                  <option key={ym} value={ym}>Přidělení: {ymLabel(ym)}</option>
                ))}
              </select>
              <select
                value={complaintClaimMonth}
                onChange={e => setComplaintClaimMonth(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                title="Měsíc reklamace"
              >
                <option value="">Reklamace: vše</option>
                {complaintClaimMonthOptions.map(ym => (
                  <option key={ym} value={ym}>Reklamace: {ymLabel(ym)}</option>
                ))}
              </select>
              <select
                value={complaintOwnerFilter}
                onChange={e => setComplaintOwnerFilter(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                title="Kdo má lead teď"
              >
                <option value="">Vlastník: vše</option>
                {complaintOwnerOptions.map(n => (
                  <option key={n} value={n}>Vlastník: {n}</option>
                ))}
              </select>
              <select
                value={complaintCallerFilter}
                onChange={e => setComplaintCallerFilter(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                title="Kdo volal"
              >
                <option value="">Volal: vše</option>
                {complaintCallerOptions.map(n => (
                  <option key={n} value={n}>Volal: {n}</option>
                ))}
              </select>
              {(complaintAssignMonth || complaintClaimMonth || complaintOwnerFilter || complaintCallerFilter) && (
                <button
                  onClick={() => { setComplaintAssignMonth(''); setComplaintClaimMonth(''); setComplaintOwnerFilter(''); setComplaintCallerFilter(''); }}
                  className={`text-sm px-3 py-1.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 hover:bg-gray-100'}`}
                >
                  ✕ Zrušit filtry
                </button>
              )}
              <button
                onClick={() => fetchLeadComplaints()}
                className="text-sm px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                🔄 Načíst
              </button>
            </div>
          </div>

          {leadComplaintsLoading ? (
            <div className="text-center py-10 text-gray-500">Načítám...</div>
          ) : leadComplaints.length === 0 ? (
            <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné reklamace leadů.</div>
          ) : filteredLeadComplaints.length === 0 ? (
            <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné reklamace neodpovídají zvoleným filtrům.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                    <th className="px-3 py-2 text-left font-semibold">Firma</th>
                    <th className="px-3 py-2 text-left font-semibold">Kdo volal</th>
                    <th className="px-3 py-2 text-left font-semibold">IČ</th>
                    <th className="px-3 py-2 text-left font-semibold">Šarže</th>
                    <th className="px-3 py-2 text-left font-semibold">Kdo má lead teď</th>
                    <th className="px-3 py-2 text-left font-semibold">Čas přidělení</th>
                    <th className="px-3 py-2 text-left font-semibold">Čas reklamace</th>
                    <th className="px-3 py-2 text-left font-semibold">Stav</th>
                    <th className="px-3 py-2 text-right font-semibold">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeadComplaints.map((c) => {
                    const statusMap = {
                      'open': { label: 'Nové', cls: isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-100 text-yellow-700' },
                      'in-progress': { label: 'V řešení', cls: isDarkMode ? 'bg-blue-800 text-blue-100' : 'bg-blue-100 text-blue-700' },
                      'approved': { label: 'Uznáno', cls: isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-700' },
                      'rejected': { label: 'Neuznáno', cls: isDarkMode ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-700' },
                    };
                    const st = statusMap[c.status] || statusMap['open'];
                    const busy = complaintActionId === c._id;
                    return (
                    <tr key={c._id} className={`border-b align-top ${c.excludedFromStats ? (isDarkMode ? 'opacity-60' : 'opacity-60') : ''} ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className="px-3 py-2 font-medium">
                        {c.companyId ? (
                          <button onClick={() => window.open(`/#/companies/${c.companyId}`, '_blank', 'noopener,noreferrer')} className="text-blue-500 hover:underline text-left">
                            {c.companyName || '—'}
                          </button>
                        ) : (c.companyName || '—')}
                        {c.description && <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>💬 {c.description}</div>}
                        {c.adminReply && <div className={`text-xs mt-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>↩ {c.adminReply}</div>}
                      </td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.calledByName || '—'}</td>
                      <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.ico || '—'}</td>
                      <td className="px-3 py-2">
                        {c.batchName ? (
                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>{c.batchName}</span>
                        ) : (<span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>—</span>)}
                      </td>
                      <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>{c.currentOwnerName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.assignedAt ? new Date(c.assignedAt).toLocaleString('cs-CZ') : '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.complainedAt ? new Date(c.complainedAt).toLocaleString('cs-CZ') : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                        {c.excludedFromStats && <div className="text-[10px] mt-1 text-gray-500">mimo statistiku</div>}
                        {c.status === 'approved' && c.settlementMonth && (
                          <div className="text-[10px] mt-1 text-gray-500">
                            {c.settlementPayer === 'partner' ? '💼 Partner' : '📞 Callcentrum'} · vyrovnání {getMonthName(c.settlementMonth)} {c.settlementYear}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end relative">
                          <button
                            onClick={(e) => {
                              if (openComplaintMenu?.id === String(c._id)) { setOpenComplaintMenu(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              setOpenComplaintMenu({ id: String(c._id), x: r.right, y: r.bottom });
                            }}
                            disabled={busy}
                            className={`px-2 py-1 rounded transition-colors border ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-600'} disabled:opacity-50`}
                            title="Akce"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

      </div>
    </div>

    {/* Detail modal */}
    {detailModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" onClick={() => setDetailModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-7xl max-h-[80vh] flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h3 className="font-bold text-base">📋 {detailModal.buttonDescription}</h3>
              {detailModal.userName && <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{detailModal.userName}</p>}
            </div>
            <button onClick={() => setDetailModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="overflow-auto flex-1 p-4">
            {detailModal.loading ? (
              <div className="text-center py-10 text-gray-500">Načítám...</div>
            ) : detailModal.companies.length === 0 ? (
              <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné firmy.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                    <th className="px-3 py-2 text-left font-semibold">Název firmy</th>
                    <th className="px-3 py-2 text-left font-semibold">IČO</th>
                    <th className="px-3 py-2 text-left font-semibold">Přiděleno</th>
                    <th className="px-3 py-2 text-left font-semibold">Čas přidělení</th>
                    <th className="px-3 py-2 text-left font-semibold">Stav</th>
                    <th className="px-3 py-2 text-left font-semibold">FC</th>
                    <th className="px-3 py-2 text-right font-semibold">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModal.companies.map((c, i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className="px-3 py-2 font-medium">{c.companyName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.ico || '—'}</td>
                      <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>{c.ownerName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.createdAt ? new Date(c.createdAt).toLocaleString('cs-CZ') : '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.status || '—'}</td>
                      <td className="px-3 py-2">
                        {c.forecast ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.forecast === '100' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.forecast}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end relative">
                          <button
                            onClick={(e) => {
                              if (openRowMenu?.id === String(c.statId)) { setOpenRowMenu(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              setOpenRowMenu({ id: String(c.statId), x: r.right, y: r.bottom });
                            }}
                            className={`px-2 py-1 rounded transition-colors border ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-600'}`}
                            title="Akce"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    )}
    {/* Hamburger menu řádku detailu (fixed popover, aby se neo\u0159\u00edzl v overflow) */}
    {openRowMenu && (() => {
      const c = (detailModal?.companies || []).find(x => String(x.statId) === openRowMenu.id);
      if (!c) return null;
      return (
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setOpenRowMenu(null)} />
          <div
            className={`fixed z-[71] rounded-lg shadow-xl border py-1 min-w-[210px] ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            style={{ top: openRowMenu.y + 4, left: Math.max(8, openRowMenu.x - 210) }}
          >
            <button
              onClick={() => { setStatMoveOwnerModal({ row: c }); setStatMoveOwnerTargetId(''); setOpenRowMenu(null); }}
              disabled={movingOwnerStatId === String(c.statId)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              🔀 Přesunout na obchodníka
            </button>
            <button
              onClick={() => { setMoveBatchModal({ row: c }); setMoveBatchTargetId(''); setOpenRowMenu(null); }}
              disabled={movingLeadId === String(c.statId)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              🏷️ Změnit šarži
            </button>
            <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
            <button
              onClick={() => { handleDeleteStat(c); setOpenRowMenu(null); }}
              disabled={deletingStatId === String(c.statId)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-orange-300' : 'hover:bg-gray-50 text-orange-600'}`}
            >
              {deletingStatId === String(c.statId) ? '⏳ Mažu…' : '🗑️ Smazat ze statistiky'}
            </button>
            <button
              onClick={() => { handleRevertLead(c); setOpenRowMenu(null); }}
              disabled={revertingLeadId === String(c.statId)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-red-300' : 'hover:bg-gray-50 text-red-600'}`}
            >
              {revertingLeadId === String(c.statId) ? '⏳ Zpracování…' : '↩️ Vrátit lead'}
            </button>
          </div>
        </>
      );
    })()}
    {/* Hamburger menu řádku reklamace (fixed popover) */}
    {openComplaintMenu && (() => {
      const c = (leadComplaints || []).find(x => String(x._id) === openComplaintMenu.id);
      if (!c) return null;
      const busy = complaintActionId === c._id;
      return (
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setOpenComplaintMenu(null)} />
          <div
            className={`fixed z-[71] rounded-lg shadow-xl border py-1 min-w-[210px] ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            style={{ top: openComplaintMenu.y + 4, left: Math.max(8, openComplaintMenu.x - 210) }}
          >
            <button
              onClick={() => { setMoveOwnerModal({ complaint: c, targetUserId: '' }); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              🔀 Přesunout na obchodníka
            </button>
            <button
              onClick={() => { setComplaintBatchModal({ complaint: c, targetButtonId: '' }); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              🏷️ Změnit šarži
            </button>
            <button
              onClick={() => { handleReturnLead(c); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              ↩️ Vrátit lead
            </button>
            <button
              onClick={() => { handleExcludeFromStats(c); setOpenComplaintMenu(null); }}
              disabled={busy || c.excludedFromStats}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-orange-300' : 'hover:bg-gray-50 text-orange-600'} disabled:opacity-40`}
            >
              🗑️ Smazat ze statistiky
            </button>
            <button
              onClick={() => { setReplyModal({ complaint: c, reply: c.adminReply || '' }); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              💬 Odpovědět
            </button>
            <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
            <button
              onClick={() => { updateLeadComplaint(c._id, { status: 'in-progress' }); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              🔵 Vyřešit: V řešení
            </button>
            <button
              onClick={() => {
                const now = new Date();
                setApproveModal({
                  complaint: c,
                  payer: c.settlementPayer || 'callcenter',
                  month: c.settlementMonth || (now.getMonth() + 1),
                  year: c.settlementYear || now.getFullYear()
                });
                setOpenComplaintMenu(null);
              }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-green-300' : 'hover:bg-gray-50 text-green-600'}`}
            >
              ✅ Vyřešit: Uznáno
            </button>
            <button
              onClick={() => { updateLeadComplaint(c._id, { status: 'rejected' }); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-red-300' : 'hover:bg-gray-50 text-red-600'}`}
            >
              ⛔ Vyřešit: Neuznáno
            </button>
            <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
            <button
              onClick={() => { handleDeleteLeadComplaint(c); setOpenComplaintMenu(null); }}
              disabled={busy}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-red-300' : 'hover:bg-gray-50 text-red-600'}`}
            >
              🗑 Smazat reklamaci
            </button>
          </div>
        </>
      );
    })()}
    {/* Move batch modal */}
    {moveBatchModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setMoveBatchModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">🏷️ Změnit šarži leadu</h3>
            <button onClick={() => setMoveBatchModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{moveBatchModal.row.companyName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Cílová šarže:</label>
              <select
                value={moveBatchTargetId}
                onChange={e => setMoveBatchTargetId(e.target.value)}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte šarži —</option>
                {(config?.buttons || []).filter(b => b.isActive && b._id !== moveBatchModal.row.buttonId).map(b => (
                  <option key={b._id} value={b._id}>{b.description} ({b.batchName}){typeof b.value === 'number' ? ` – ${b.value} Kč` : ''}</option>
                ))}
              </select>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Změna šarže upraví i cenu leadu – promítne se do provizí.</p>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => setMoveBatchModal(null)}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={handleMoveLead}
                disabled={!moveBatchTargetId || movingLeadId === String(moveBatchModal.row.statId)}
                className="px-4 py-2 rounded text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-semibold disabled:bg-gray-400"
              >
                {movingLeadId === String(moveBatchModal.row.statId) ? 'Ukládám...' : 'Změnit šarži'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Stat move owner modal (přesun leadu na jiného obchodníka) */}
    {statMoveOwnerModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setStatMoveOwnerModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">🔀 Přesunout lead na obchodníka</h3>
            <button onClick={() => setStatMoveOwnerModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{statMoveOwnerModal.row.companyName || '—'}</span>
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Současný obchodník: <span className="font-semibold">{statMoveOwnerModal.row.ownerName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Nový obchodník:</label>
              <select
                value={statMoveOwnerTargetId}
                onChange={e => setStatMoveOwnerTargetId(e.target.value)}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte obchodníka —</option>
                {allUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => setStatMoveOwnerModal(null)}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={handleMoveStatOwner}
                disabled={!statMoveOwnerTargetId || movingOwnerStatId === String(statMoveOwnerModal.row.statId)}
                className="px-4 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400"
              >
                {movingOwnerStatId === String(statMoveOwnerModal.row.statId) ? 'Přesouvám...' : 'Přesunout'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Reply to lead complaint modal */}
    {replyModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setReplyModal(null)}>
        <div className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">↩ Odpověď na reklamaci</h3>
            <button onClick={() => setReplyModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{replyModal.complaint.companyName || '—'}</span>
            </p>
            {replyModal.complaint.description && (
              <div className={`text-sm p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <span className="font-medium">Reklamace: </span>{replyModal.complaint.description}
              </div>
            )}
            <textarea
              value={replyModal.reply}
              onChange={e => setReplyModal({ ...replyModal, reply: e.target.value })}
              rows={4}
              placeholder="Napište odpověď obchodníkovi…"
              className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setReplyModal(null)} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Zrušit</button>
              <button onClick={handleSaveReply} disabled={replySaving} className="px-4 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {replySaving ? 'Ukládám...' : 'Uložit odpověď'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Move lead to another owner modal */}
    {moveOwnerModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setMoveOwnerModal(null)}>
        <div className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">🔀 Přesunout lead na obchodníka</h3>
            <button onClick={() => setMoveOwnerModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{moveOwnerModal.complaint.companyName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Nový vlastník:</label>
              <select
                value={moveOwnerModal.targetUserId}
                onChange={e => setMoveOwnerModal({ ...moveOwnerModal, targetUserId: e.target.value })}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte uživatele —</option>
                {allUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setMoveOwnerModal(null)} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Zrušit</button>
              <button onClick={handleMoveOwner} disabled={!moveOwnerModal.targetUserId} className="px-4 py-2 rounded text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-semibold disabled:bg-gray-400">Přesunout</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Change complaint batch modal */}
    {complaintBatchModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setComplaintBatchModal(null)}>
        <div className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">🏷️ Změnit šarži leadu</h3>
            <button onClick={() => setComplaintBatchModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{complaintBatchModal.complaint.companyName || '—'}</span>
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Aktuální šarže: <span className="font-semibold">{complaintBatchModal.complaint.batchName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Cílová šarže:</label>
              <select
                value={complaintBatchModal.targetButtonId}
                onChange={e => setComplaintBatchModal({ ...complaintBatchModal, targetButtonId: e.target.value })}
                className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte šarži —</option>
                {(config?.buttons || []).filter(b => b.isActive && b.batchName !== complaintBatchModal.complaint.batchName).map(b => (
                  <option key={b._id} value={b._id}>{b.description} ({b.batchName}){typeof b.value === 'number' ? ` – ${b.value} Kč` : ''}</option>
                ))}
              </select>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Změna šarže upraví i cenu leadu – promítne se do provizí.</p>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setComplaintBatchModal(null)} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Zrušit</button>
              <button onClick={handleChangeComplaintBatch} disabled={!complaintBatchModal.targetButtonId || complaintActionId === complaintBatchModal.complaint._id} className="px-4 py-2 rounded text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-semibold disabled:bg-gray-400">
                {complaintActionId === complaintBatchModal.complaint._id ? 'Ukládám...' : 'Změnit šarži'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Approve complaint with settlement modal */}
    {approveModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setApproveModal(null)}>
        <div className={`rounded-xl shadow-2xl w-full max-w-md ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">✅ Uznat reklamaci</h3>
            <button onClick={() => setApproveModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Firma: <span className="font-semibold">{approveModal.complaint.companyName || '—'}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Kdo náklad nese?</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${approveModal.payer === 'callcenter' ? (isDarkMode ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50') : (isDarkMode ? 'border-gray-600' : 'border-gray-200')}`}>
                  <input type="radio" name="payer" checked={approveModal.payer === 'callcenter'} onChange={() => setApproveModal({ ...approveModal, payer: 'callcenter' })} className="mt-1" />
                  <span className="text-sm">
                    <span className="font-semibold">Callcentrum ({approveModal.complaint.calledByName || 'telefonista'})</span>
                    <span className={`block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Strhne se z provize callcentra + OZ se vrátí náklad za lead.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${approveModal.payer === 'partner' ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50') : (isDarkMode ? 'border-gray-600' : 'border-gray-200')}`}>
                  <input type="radio" name="payer" checked={approveModal.payer === 'partner'} onChange={() => setApproveModal({ ...approveModal, payer: 'partner' })} className="mt-1" />
                  <span className="text-sm">
                    <span className="font-semibold">Partner (naše chyba)</span>
                    <span className={`block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Callcentrum si provizi nechá, jen OZ se vrátí náklad za lead.</span>
                  </span>
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Měsíc vyrovnání</label>
                <select
                  value={approveModal.month}
                  onChange={e => setApproveModal({ ...approveModal, month: parseInt(e.target.value) })}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{getMonthName(m)}</option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className="block text-sm font-medium mb-1">Rok</label>
                <select
                  value={approveModal.year}
                  onChange={e => setApproveModal({ ...approveModal, year: parseInt(e.target.value) })}
                  className={`w-full px-3 py-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Korekce se projeví ve zvoleném měsíci jako samostatná záporná položka — původní (už proplacený) měsíc zůstane beze změny.
            </p>
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setApproveModal(null)} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Zrušit</button>
              <button onClick={handleApproveWithSettlement} disabled={complaintActionId === approveModal.complaint._id} className="px-4 py-2 rounded text-sm bg-green-600 hover:bg-green-700 text-white font-semibold disabled:bg-gray-400">
                {complaintActionId === approveModal.complaint._id ? 'Ukládám...' : 'Uznat a naplánovat vyrovnání'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Manual add lead modal */}
    {showAddLeadModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4" onClick={() => setShowAddLeadModal(false)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-lg ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">➕ Přidat lead manuálně</h3>
            <button onClick={() => setShowAddLeadModal(false)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="p-5 space-y-4">
            {/* Company search */}
            <div>
              <label className="block text-sm font-medium mb-1">Hledat firmu (IČO nebo název):</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={addLeadForm.searchQuery}
                  onChange={e => setAddLeadForm(prev => ({ ...prev, searchQuery: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && searchCompanyForLead()}
                  placeholder="Zadejte IČO nebo název firmy..."
                  className={`flex-1 px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
                />
                <button
                  onClick={searchCompanyForLead}
                  disabled={addLeadSearchLoading}
                  className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:bg-gray-400"
                >
                  {addLeadSearchLoading ? '...' : 'Hledat'}
                </button>
              </div>
              {addLeadForm.foundCompanies.length > 0 && !addLeadForm.selectedCompany && (
                <div className={`mt-2 rounded border max-h-40 overflow-y-auto ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
                  {addLeadForm.foundCompanies.map(c => (
                    <div
                      key={c._id}
                      className={`px-3 py-2 cursor-pointer text-sm hover:${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} border-b last:border-0 ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}
                      onClick={() => setAddLeadForm(prev => ({ ...prev, selectedCompany: c, foundCompanies: [] }))}
                    >
                      <span className="font-medium">{c.name}</span>
                      {c.ico && <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>IČO: {c.ico}</span>}
                    </div>
                  ))}
                </div>
              )}
              {addLeadForm.selectedCompany && (
                <div className={`mt-2 px-3 py-2 rounded text-sm flex justify-between items-center ${isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-50 text-green-800'}`}>
                  <span>✓ <strong>{addLeadForm.selectedCompany.name}</strong>{addLeadForm.selectedCompany.ico ? ` (IČO: ${addLeadForm.selectedCompany.ico})` : ''}</span>
                  <button onClick={() => setAddLeadForm(prev => ({ ...prev, selectedCompany: null, searchQuery: '' }))} className="text-xs underline">Změnit</button>
                </div>
              )}
            </div>

            {/* Batch selection */}
            <div>
              <label className="block text-sm font-medium mb-1">Šarže / typ leadu:</label>
              <select
                value={addLeadForm.selectedButtonId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, selectedButtonId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte šarži —</option>
                {(config?.buttons || []).filter(b => b.isActive).map(b => (
                  <option key={b._id} value={b._id}>{b.description} ({b.batchName}) — {b.value} Kč</option>
                ))}
              </select>
            </div>

            {/* Callcenter user */}
            <div>
              <label className="block text-sm font-medium mb-1">Kdo lead udělal (callcenter):
                <span className={`ml-1 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>→ určuje řádek ve statistice</span>
              </label>
              <select
                value={addLeadForm.callcenterUserId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, callcenterUserId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte uživatele —</option>
                {allUsers.filter(u => u.role === 'callcentrum').map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
                {allUsers.filter(u => u.role === 'callcentrum').length === 0 && allUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            {/* Assigned-to user */}
            <div>
              <label className="block text-sm font-medium mb-1">Komu šel lead (obchodník):</label>
              <select
                value={addLeadForm.assignedToUserId}
                onChange={e => setAddLeadForm(prev => ({ ...prev, assignedToUserId: e.target.value }))}
                className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">— Vyberte uživatele —</option>
                {allUsers.filter(u => u.role !== 'callcentrum').map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Lead bude přidán do měsíce <strong>{getMonthName(selectedMonth)} {selectedYear}</strong>
            </p>

            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => { setShowAddLeadModal(false); setAddLeadForm({ searchQuery: '', foundCompanies: [], selectedCompany: null, selectedButtonId: '', callcenterUserId: '', assignedToUserId: '' }); }}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={handleAddManualLead}
                disabled={addLeadSubmitting || !addLeadForm.selectedCompany || !addLeadForm.selectedButtonId || !addLeadForm.callcenterUserId || !addLeadForm.assignedToUserId}
                className="px-4 py-2 rounded text-sm bg-green-600 hover:bg-green-700 text-white font-semibold disabled:bg-gray-400"
              >
                {addLeadSubmitting ? 'Přidávám...' : 'Přidat lead'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default CallCenterAdmin;
